import React, { useState } from 'react';
import { Save, Image as ImageIcon, MapPin, Clock, Link, Share2, Palette, Eye, EyeOff, Upload, Copy } from 'lucide-react';
import { useConfig } from '../hooks/useConfig';
import { supabase } from '../supabaseClient';

export default function MinisitioPage() {
  const { 
    activeBusiness, 
    activeBusinessId,
    updateActiveBusinessField,
    saveBusinessSettings,
    showNotification
  } = useConfig();

  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const processAndUploadImage = async (file, type) => {
    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `menu_minisitio_${type}_${activeBusinessId}_${Date.now()}.${fileExt}`;
      const filePath = `menus/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('menu-files')
        .upload(filePath, file, { upsert: true });
        
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('menu-files')
        .getPublicUrl(filePath);
        
      return publicUrl;
    } catch (err) {
      console.error("Upload error:", err);
      throw err;
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (type === 'banner') setIsUploadingBanner(true);
    else setIsUploadingLogo(true);
    
    try {
      const publicUrl = await processAndUploadImage(file, type);
      if (type === 'banner') {
        updateActiveBusinessField('banner_url', publicUrl);
        await saveBusinessSettings({ banner_url: publicUrl });
      } else {
        updateActiveBusinessField('logo_url', publicUrl);
        await saveBusinessSettings({ logo_url: publicUrl });
      }
      if (showNotification) showNotification(`¡${type === 'banner' ? 'Banner' : 'Logo'} subido con éxito!`, "success");
    } catch (error) {
      console.error("Detalle del error:", error);
      if (showNotification) showNotification(`Error de Supabase: ${error.message || 'Desconocido'}`, "error");
    } finally {
      if (type === 'banner') setIsUploadingBanner(false);
      else setIsUploadingLogo(false);
      e.target.value = '';
    }
  };

  if (!activeBusiness) return <div>Cargando...</div>;

  const siteUrl = activeBusiness.slug ? `${window.location.origin}/app/${activeBusiness.slug}` : '';
  const copySiteUrl = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
      if (showNotification) showNotification('Enlace copiado', 'success');
    } catch {
      if (showNotification) showNotification('No se pudo copiar; selecciona el enlace y cópialo a mano.', 'error');
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading">Personalizar Mi Sitio Web</h2>
          <p className="text-xs text-seryi-text">Edita la apariencia, redes sociales y ubicación que verán tus clientes en el portal de reservas.</p>
        </div>
        {activeBusiness.slug && (
          <a 
            href={`${window.location.origin}/app/${activeBusiness.slug}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-shrink-0 bg-seryi-green/10 text-seryi-green hover:bg-seryi-green/20 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-colors"
          >
            <Eye size={14} />
            Ver mi Minisitio
          </a>
        )}
      </div>

      <div className="bg-seryi-bg border border-seryi-bg-light p-4 rounded-3xl space-y-2">
        <span className="text-[10px] uppercase font-bold text-seryi-text">Enlace de tu sitio</span>
        {siteUrl ? (
          <div className="flex items-center gap-2">
            <input readOnly aria-label="Enlace de tu sitio" value={siteUrl} onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading font-mono" />
            <button type="button" onClick={copySiteUrl}
              className="shrink-0 bg-seryi-green/10 text-seryi-green hover:bg-seryi-green/20 px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors">
              <Copy size={14} /> Copiar
            </button>
          </div>
        ) : (
          <p className="text-xs text-seryi-text">Aún no tienes enlace. Define el «URL / Slug Público» en Configuración (por ejemplo <b>aguapura</b>), guarda, y aquí aparecerá tu enlace.</p>
        )}
      </div>

      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-6">
        
        {/* URL IMAGENES */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex flex-col gap-0.5">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-1.5"><ImageIcon size={12} /> URL de Banner / Portada</div>
                <label className="cursor-pointer bg-seryi-green/10 text-seryi-green hover:bg-seryi-green/20 px-2 py-1 rounded flex items-center gap-1 transition-colors">
                  {isUploadingBanner ? <div className="w-3 h-3 border-2 border-seryi-green border-t-transparent rounded-full animate-spin" /> : <Upload size={10} />}
                  <span className="text-[9px]">{isUploadingBanner ? 'Subiendo...' : 'Subir Foto'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'banner')} disabled={isUploadingBanner} />
                </label>
              </div>
              <span className="text-[9px] font-normal text-slate-500 normal-case">Recomendado: 1200x400px (Formato horizontal ancho)</span>
            </label>
            <input 
              type="text"
              placeholder="https://..."
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green mb-2"
              value={activeBusiness.banner_url || ''}
              onChange={(e) => updateActiveBusinessField('banner_url', e.target.value)}
            />
            {activeBusiness.banner_url && (
              <div className="w-full h-24 bg-seryi-bg-dark rounded-xl border border-white/5 overflow-hidden">
                <img src={activeBusiness.banner_url} alt="Banner Preview" className="w-full h-full object-cover opacity-80" />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex flex-col gap-0.5">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-1.5"><ImageIcon size={12} /> URL de Logo del Negocio</div>
                <label className="cursor-pointer bg-seryi-green/10 text-seryi-green hover:bg-seryi-green/20 px-2 py-1 rounded flex items-center gap-1 transition-colors">
                  {isUploadingLogo ? <div className="w-3 h-3 border-2 border-seryi-green border-t-transparent rounded-full animate-spin" /> : <Upload size={10} />}
                  <span className="text-[9px]">{isUploadingLogo ? 'Subiendo...' : 'Subir Foto'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'logo')} disabled={isUploadingLogo} />
                </label>
              </div>
              <span className="text-[9px] font-normal text-slate-500 normal-case">Recomendado: 400x400px (Formato cuadrado 1:1)</span>
            </label>
            <input 
              type="text"
              placeholder="https://..."
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green mb-2"
              value={activeBusiness.logo_url || ''}
              onChange={(e) => updateActiveBusinessField('logo_url', e.target.value)}
            />
            {activeBusiness.logo_url && (
              <div className="w-16 h-16 bg-seryi-bg-dark rounded-xl border border-white/5 overflow-hidden p-1 shadow-md">
                <img src={activeBusiness.logo_url} alt="Logo Preview" className="w-full h-full object-cover rounded-lg" />
              </div>
            )}
          </div>
        </div>

        {/* UBICACION / HORARIO */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5">
              <Clock size={12} /> Horario de Atención
            </label>
            <input 
              type="text"
              placeholder="Ej: Lun-Sáb 8:00 AM - 8:00 PM"
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={activeBusiness.horario_atencion || activeBusiness.horario || ''}
              onChange={(e) => {
                updateActiveBusinessField('horario_atencion', e.target.value);
                updateActiveBusinessField('horario', e.target.value);
              }}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5">
              <MapPin size={12} /> Dirección / Ubicación
            </label>
            <input 
              type="text"
              placeholder="Ej: Cra 45 # 12-34, Bogotá"
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={activeBusiness.address || ''}
              onChange={(e) => updateActiveBusinessField('address', e.target.value)}
            />
          </div>
        </div>

        {/* REDES SOCIALES */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5">
              <Share2 size={12} /> Instagram
            </label>
            <input 
              type="text"
              placeholder="@usuario o https://..."
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={activeBusiness.instagram || ''}
              onChange={(e) => updateActiveBusinessField('instagram', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5">
              <Link size={12} /> Facebook
            </label>
            <input 
              type="text"
              placeholder="https://..."
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={activeBusiness.facebook || ''}
              onChange={(e) => updateActiveBusinessField('facebook', e.target.value)}
            />
          </div>
        </div>

        {/* APARIENCIA Y COMPORTAMIENTO */}
        <div className="border-t border-seryi-bg-light pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5">
              <Palette size={12} /> Color Principal (Acento)
            </label>
            <div className="flex items-center gap-3">
              <input 
                type="color"
                className="w-10 h-10 rounded cursor-pointer bg-seryi-bg-dark border border-seryi-bg-light p-1"
                value={activeBusiness.minisite_theme_color || '#00E267'}
                onChange={(e) => updateActiveBusinessField('minisite_theme_color', e.target.value)}
              />
              <span className="text-xs text-seryi-text font-mono">{activeBusiness.minisite_theme_color || '#00E267'}</span>
            </div>
          </div>
          
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5 mb-2">
              <Eye size={12} /> Mostrar Staff/Equipo
            </label>
            <div 
              className={`w-14 h-7 rounded-full p-1 cursor-pointer transition-colors ${
                (activeBusiness.minisite_show_staff ?? true) ? 'bg-seryi-green' : 'bg-slate-700'
              }`}
              onClick={() => updateActiveBusinessField('minisite_show_staff', !(activeBusiness.minisite_show_staff ?? true))}
            >
              <div 
                className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  (activeBusiness.minisite_show_staff ?? true) ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </div>
            <p className="text-[10px] text-seryi-text mt-1">
              Si está desactivado, los clientes no elegirán con quién atenderse.
            </p>
          </div>
        </div>

        <div className="pt-4">
          <button 
            onClick={() => saveBusinessSettings()}
            className="w-full bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-black py-3.5 px-6 rounded-xl text-xs flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95"
          >
            <Save size={16} /> Guardar Personalización del Sitio Web
          </button>
        </div>
      </div>
    </div>
  );
}
